import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import CalendarWidget from "../components/CalendarWidget";
import {
  Bell,
  Calendar,
  User,
  MessageSquare,
  Briefcase,
  ChevronLeft,
  ChevronRight,
  Send,
  Info,
  Target,
  Clock,
  AlertCircle,
  CheckCircle,
  X,
  Pencil,
  Trash2,
  Plus,
  Pin,
  CalendarPlus,
} from "lucide-react";
import { toast } from "sonner";
import ConfirmModal from "../components/ConfirmModal";

// Fecha local sin desplazamiento por timezone (YYYY-MM-DD → Date en mediodía local)
const parseDateLocal = (dateStr) => {
  if (!dateStr) return null;
  const s = String(dateStr).slice(0, 10);
  return new Date(s + "T12:00:00");
};
const todayDateString = () => {
  const d = new Date();
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
};

// Convierte texto con URLs en elementos React; las URLs son enlaces clicables
const linkifyDescription = (text) => {
  if (!text || typeof text !== "string") return null;
  const parts = text.split(/(https?:\/\/[^\s]+)/g);
  return parts.map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a key={i} href={part.trim()} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all">{part}</a>
    ) : (
      part
    )
  );
};

// Services available - Default services (moved outside component to avoid dependency issues)
const defaultServices = [
  { id: 1, name: "Soporte Técnico", icon: "Briefcase", description: "Ayuda con la plataforma", contact: "soporte@ecosistemawca.com" },
  { id: 2, name: "Recursos Humanos", icon: "User", description: "Consultas de personal", contact: "rh@ecosistemawca.com" },
  { id: 3, name: "Academia", icon: "Target", description: "Programas educativos", contact: "academia@ecosistemawca.com" },
  { id: 4, name: "Marketing", icon: "MessageSquare", description: "Medios y difusión", contact: "marketing@ecosistemawca.com" },
];

// Opciones de tipo para Avisos Importantes (tab Avisos)
const NOTICE_TYPE_OPTIONS = [
  { value: "evento", label: "Evento" },
  { value: "aviso", label: "Aviso" },
  { value: "comunicado", label: "Comunicado" },
  { value: "info", label: "Info" },
  { value: "warning", label: "Advertencia" },
];
// Opciones de tipo para Todos los Anuncios (tab Avisos)
const ANNOUNCEMENT_TYPE_OPTIONS = [
  { value: "anuncio", label: "Anuncio" },
  { value: "evento", label: "Evento" },
  { value: "roles_administrativo", label: "Roles/administrativo" },
  { value: "general", label: "General" },
  { value: "comunicado", label: "Comunicado" },
  { value: "otro", label: "Otro" },
];

// Carousel default (fallback when backend has none)
const defaultCarouselSlides = [
  { id: "default-1", url: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800", title: "Comunidad WCA", description: "Construyendo el futuro juntos" },
  { id: "default-2", url: "https://images.unsplash.com/photo-1531482615713-2afd69097998?w=800", title: "Innovación Educativa", description: "Transformando la educación" },
  { id: "default-3", url: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800", title: "Trabajo en Equipo", description: "El poder de la colaboración" },
  { id: "default-4", url: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800", title: "Impacto Social", description: "Generando cambio positivo" },
];

const InformacionGeneral = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("inicio");
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportType, setReportType] = useState("sugerencia");
  const [reportContent, setReportContent] = useState("");
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [reportSubmitted, setReportSubmitted] = useState(false);
  const [reportSending, setReportSending] = useState(false);
  const [selectedCalendarEvents, setSelectedCalendarEvents] = useState([]);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);

  // State for dynamic data from backend
  const [announcements, setAnnouncements] = useState([]);
  const [notices, setNotices] = useState([]);
  const [services, setServices] = useState(defaultServices);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [carouselSlides, setCarouselSlides] = useState([]);
  const [carouselLoaded, setCarouselLoaded] = useState(false); // evita flash de predeterminadas hasta tener respuesta del backend
  const [removedDefaultIds, setRemovedDefaultIds] = useState([]); // IDs de predeterminadas "eliminadas" en el modal (solo sesión)

  // SuperAdmin CRUD modals state (declared before useEffects that use them)
  const [modalAnnouncement, setModalAnnouncement] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [modalNotice, setModalNotice] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  const [modalService, setModalService] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [modalEvent, setModalEvent] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [modalCarouselSlide, setModalCarouselSlide] = useState(false);
  const [editingCarouselSlide, setEditingCarouselSlide] = useState(undefined);
  const [eventDatePreset, setEventDatePreset] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formAnnouncement, setFormAnnouncement] = useState({ title: "", date: new Date().toLocaleDateString("es-ES"), type: "anuncio", urgent: false });
  const [formNotice, setFormNotice] = useState({ title: "", content: "", date: new Date().toLocaleDateString("es-ES"), type: "aviso", pinned: false, valid_until: "", event_date: "", event_time: "" });
  const [formService, setFormService] = useState({ name: "", description: "", contact: "", icon: "Briefcase" });
  const [formEvent, setFormEvent] = useState({ title: "", date: todayDateString(), time: "", location: "", description: "" });
  const [formCarouselSlide, setFormCarouselSlide] = useState({ url: "", title: "", description: "" });
  const [confirmModal, setConfirmModal] = useState({ open: false, title: "", message: "", onConfirm: null });
  const [eventDetailModal, setEventDetailModal] = useState(null); // evento para mostrar descripción al hacer clic
  const [filterAnnouncementType, setFilterAnnouncementType] = useState(""); // filtro en "Todos los Anuncios": "" = todos
  const [addingToGoogleCalendar, setAddingToGoogleCalendar] = useState(false);

  const handleAddToGoogleCalendar = async (event) => {
    if (!event?.id) return;
    setAddingToGoogleCalendar(true);
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
      const res = await axios.post(`${API}/calendar/events/${event.id}/add-to-google-calendar`, { timezone }, { withCredentials: true });
      if (res.data?.already_added) {
        toast.info("Este evento ya está en tu calendario");
      } else {
        toast.success("Evento añadido a tu Google Calendar");
      }
    } catch (e) {
      if (e.response?.status === 403 && e.response?.data?.detail === "calendar_not_linked") {
        toast.error("Vincula tu Google Calendar en Permisos para usar esta función");
        navigate("/permisos", { replace: true });
      } else {
        toast.error(e.response?.data?.detail || "No se pudo añadir al calendario");
      }
    } finally {
      setAddingToGoogleCalendar(false);
    }
  };

  const fetchGeneralData = async () => {
    try {
      const [announcementsRes, noticesRes, servicesRes, eventsRes, carouselRes] = await Promise.all([
        axios.get(`${API}/announcements`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/notices`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/services`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/calendar/events`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/carousel`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setAnnouncements(announcementsRes.data || []);
      setNotices(noticesRes.data || []);
      setServices(servicesRes.data?.length > 0 ? servicesRes.data : defaultServices);
      setCalendarEvents(eventsRes.data || []);
      setCarouselSlides(carouselRes.data || []);
      setCarouselLoaded(true);
    } catch (error) {
      if (error.response?.status !== 404) console.error("Error fetching general data:", error);
      setAnnouncements([]);
      setNotices([]);
      setServices(defaultServices);
      setCalendarEvents([]);
      setCarouselSlides([]);
      setCarouselLoaded(true);
    }
  };

  useEffect(() => {
    fetchGeneralData();
  }, []);

  // Sync form state when opening CRUD modals
  useEffect(() => {
    if (modalAnnouncement) {
      setFormAnnouncement(editingAnnouncement ? { title: editingAnnouncement.title, date: editingAnnouncement.date, type: editingAnnouncement.type || "anuncio", urgent: !!editingAnnouncement.urgent } : { title: "", date: new Date().toLocaleDateString("es-ES"), type: "anuncio", urgent: false });
    }
  }, [modalAnnouncement, editingAnnouncement]);
  useEffect(() => {
    if (modalNotice) {
      setFormNotice(editingNotice
        ? { title: editingNotice.title, content: editingNotice.content, date: editingNotice.date, type: editingNotice.type || "aviso", pinned: !!editingNotice.pinned, valid_until: editingNotice.valid_until || "", event_date: editingNotice.event_date || "", event_time: editingNotice.event_time || "" }
        : { title: "", content: "", date: new Date().toLocaleDateString("es-ES"), type: "aviso", pinned: false, valid_until: "", event_date: "", event_time: "" });
    }
  }, [modalNotice, editingNotice]);
  useEffect(() => {
    if (modalService) {
      setFormService(editingService ? { name: editingService.name, description: editingService.description, contact: editingService.contact, icon: editingService.icon || "Briefcase" } : { name: "", description: "", contact: "", icon: "Briefcase" });
    }
  }, [modalService, editingService]);
  useEffect(() => {
    if (modalEvent) {
      if (editingEvent) setFormEvent({ title: editingEvent.title, date: (editingEvent.date || "").toString().slice(0, 10), time: editingEvent.time || "", location: editingEvent.location || "", description: editingEvent.description || "" });
      else if (eventDatePreset) setFormEvent({ title: "", date: eventDatePreset.getFullYear() + "-" + String(eventDatePreset.getMonth() + 1).padStart(2, "0") + "-" + String(eventDatePreset.getDate()).padStart(2, "0"), time: "", location: "", description: "" });
      else setFormEvent({ title: "", date: todayDateString(), time: "", location: "", description: "" });
    }
  }, [modalEvent, editingEvent, eventDatePreset]);
  useEffect(() => {
    if (modalCarouselSlide) {
      setFormCarouselSlide(editingCarouselSlide ? { url: editingCarouselSlide.url, title: editingCarouselSlide.title, description: editingCarouselSlide.description } : { url: "", title: "", description: "" });
    }
  }, [modalCarouselSlide, editingCarouselSlide]);

  // Display carousel: solo después de cargar; backend o predeterminadas (menos las "eliminadas" en el modal)
  const carouselImages = !carouselLoaded
    ? []
    : carouselSlides.length > 0
      ? carouselSlides
      : defaultCarouselSlides.filter((d) => !removedDefaultIds.includes(d.id));

  // Auto carousel
  useEffect(() => {
    if (carouselImages.length > 0) {
      const timer = setInterval(() => {
        setCarouselIndex((prev) => (prev + 1) % carouselImages.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [carouselImages.length]);

  const handleSubmitReport = async () => {
    if (!reportContent.trim()) return;
    setReportSending(true);
    try {
      await axios.post(`${API}/reports`, { type: reportType, content: reportContent.trim() }, { withCredentials: true });
      setReportSubmitted(true);
      toast.success("Reporte enviado correctamente");
      setTimeout(() => {
        setShowReportModal(false);
        setReportSubmitted(false);
        setReportContent("");
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar el reporte");
    } finally {
      setReportSending(false);
    }
  };

  const handleCalendarDateSelect = (date, events) => {
    setSelectedCalendarDate(date);
    setSelectedCalendarEvents(events);
  };

  // Eventos de hoy (para la columna derecha cuando no hay día seleccionado)
  const eventsToday = calendarEvents.filter(e => String(e.date || "").slice(0, 10) === todayDateString());
  const displayDate = selectedCalendarDate || new Date();
  const displayDayEvents = selectedCalendarDate ? selectedCalendarEvents : eventsToday;
  const displayDateLabel = selectedCalendarDate
    ? selectedCalendarDate.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })
    : "Eventos de hoy";

  const isSuperAdmin = user?.is_super_admin || user?.email === "contacto@ecosistemawca.com" || user?.email === "ecosistemawca@gmail.com";

  const getRoleLabel = (role) => {
    const roles = {
      admin: "Administrador",
      director: "Director",
      member: "Miembro",
      pending: "Pendiente"
    };
    return roles[role] || role;
  };

  // Nombres completos; incluye alias ops/data por compatibilidad con datos antiguos
  const getDirectionNames = () => {
    const names = {
      general: "Dirección General",
      operaciones: "Dirección de Operaciones y Logística",
      ops: "Dirección de Operaciones y Logística",
      talento: "Dirección de Talento y Bienestar",
      academia: "Dirección Académica y de Innovación Educativa",
      comunicacion: "Dirección de Comunicación y Experiencia",
      finanzas: "Dirección de Estrategia y Finanzas",
      informacion: "Dirección de Investigación e Información",
      data: "Dirección de Investigación e Información",
      tecnologia: "Dirección de Desarrollo Tecnológico e Innovación",
    };
    return user?.assigned_directions?.map(d => names[d] || d).join(", ") || "Sin asignar";
  };

  // Próximos eventos (fecha local para evitar desfase de un día)
  const upcomingEvents = calendarEvents
    .filter(e => e.date && parseDateLocal(e.date) >= parseDateLocal(todayDateString()))
    .sort((a, b) => parseDateLocal(a.date) - parseDateLocal(b.date))
    .slice(0, 5);

  return (
    <div className="space-y-6 pt-4 sm:pt-0 pb-4 sm:pb-4" data-testid="general-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Bienvenido al Ecosistema WCA</h1>
          <p className="text-slate-500 text-sm sm:text-base">Portal de información general</p>
        </div>
      </div>

      {/* User Info Card */}
      <div className="glass-panel p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.name}
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl object-cover"
            />
          ) : (
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
              <span className="text-white font-bold text-2xl sm:text-3xl">{user?.name?.charAt(0)}</span>
            </div>
          )}
          <div className="flex-1">
            <h2 className="text-lg sm:text-xl font-bold text-slate-800">{user?.name}</h2>
            <p className="text-slate-500 text-sm">{user?.email}</p>
            <div className="flex flex-wrap gap-2 mt-3">
              <span className="badge badge-primary">{getRoleLabel(user?.role)}</span>
              {user?.is_super_admin && (
                <span className="badge bg-violet-100 text-violet-700">Super Admin</span>
              )}
            </div>
          </div>
          <div className="w-full sm:w-auto text-left sm:text-right mt-2 sm:mt-0">
            <p className="text-xs text-slate-400 uppercase tracking-wider">Áreas asignadas</p>
            <p className="text-sm text-slate-600 mt-1">{getDirectionNames()}</p>
          </div>
        </div>
      </div>

      {/* Tabs - Responsive: en móvil ancho completo y centrado como las demás tarjetas */}
      <div className="w-full">
        <div className="glass-panel p-1 flex w-full rounded-full">
          {[
            { id: "inicio", label: "Inicio", icon: Info },
            { id: "avisos", label: "Avisos", icon: Bell },
            { id: "calendario", label: "Calendario", icon: Calendar },
            { id: "servicios", label: "Servicios", icon: Briefcase },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 min-w-0 px-2 sm:px-6 py-2 sm:py-3 rounded-full transition-all text-sm sm:text-base ${
                activeTab === tab.id
                  ? "bg-primary text-white"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={16} className="sm:w-[18px] sm:h-[18px] flex-shrink-0" />
              <span className="hidden xs:inline truncate">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Inicio Tab */}
      {activeTab === "inicio" && (
        <div className="space-y-6">
          {/* Carousel: no se muestran predeterminadas hasta cargar (evita parpadeo al recargar) */}
          <div className="glass-panel overflow-hidden relative">
            {isSuperAdmin && (
              <button
                type="button"
                onClick={() => { setEditingCarouselSlide(undefined); setModalCarouselSlide(true); }}
                className="absolute top-3 right-3 z-10 p-2 rounded-lg bg-white/90 hover:bg-white text-slate-700 shadow-sm"
                title="Gestionar carrusel"
              >
                <Pencil size={18} />
              </button>
            )}
            <div className="relative aspect-[16/9] sm:aspect-[21/9] overflow-hidden">
              {!carouselLoaded || carouselImages.length === 0 ? (
                <div className="absolute inset-0 bg-slate-200 animate-pulse" aria-hidden="true" />
              ) : (
                <>
                  <div key={carouselIndex} className="absolute inset-0 animate-carousel-fade">
                    <img
                      src={carouselImages[carouselIndex]?.url}
                      alt={carouselImages[carouselIndex]?.title || ""}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 text-white animate-carousel-fade" key={`caption-${carouselIndex}`}>
                    <h3 className="text-lg sm:text-2xl font-bold">{carouselImages[carouselIndex]?.title || ""}</h3>
                    <p className="text-sm sm:text-base text-white/80">{carouselImages[carouselIndex]?.description || ""}</p>
                  </div>
                  <button
                    onClick={() => setCarouselIndex((prev) => (prev - 1 + carouselImages.length) % carouselImages.length)}
                    className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 p-2 sm:p-3 bg-white/20 backdrop-blur-md rounded-full hover:bg-white/40 transition-colors"
                  >
                    <ChevronLeft size={20} className="text-white sm:w-6 sm:h-6" />
                  </button>
                  <button
                    onClick={() => setCarouselIndex((prev) => (prev + 1) % carouselImages.length)}
                    className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-2 sm:p-3 bg-white/20 backdrop-blur-md rounded-full hover:bg-white/40 transition-colors"
                  >
                    <ChevronRight size={20} className="text-white sm:w-6 sm:h-6" />
                  </button>
                  <div className="absolute bottom-16 sm:bottom-20 left-1/2 -translate-x-1/2 flex gap-2">
                    {carouselImages.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCarouselIndex(i)}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          i === carouselIndex ? "bg-white" : "bg-white/40"
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Anuncios Recientes - arriba de las 4 tarjetas */}
          <div className="glass-panel p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-800">Anuncios Recientes</h3>
              <div className="flex items-center gap-2">
                {isSuperAdmin && (
                  <button
                    onClick={() => { setEditingAnnouncement(null); setModalAnnouncement(true); }}
                    className="btn-primary text-sm py-1.5 px-3 flex items-center gap-1"
                  >
                    <Plus size={16} /> Agregar
                  </button>
                )}
                <button
                  onClick={() => setActiveTab("avisos")}
                  className="text-primary text-sm hover:underline"
                >
                  Ver todos
                </button>
              </div>
            </div>
            <div className="space-y-3">
              {announcements.length > 0 ? (
                announcements.slice(0, 3).map((announcement) => {
                  const typeLabel = ANNOUNCEMENT_TYPE_OPTIONS.find(o => o.value === announcement.type)?.label || announcement.type;
                  const badgeClass = announcement.type === "evento" ? "badge-primary" : announcement.type === "anuncio" ? "badge-success" : announcement.type === "roles_administrativo" ? "bg-amber-100 text-amber-800" : announcement.type === "general" ? "bg-sky-100 text-sky-800" : announcement.type === "comunicado" ? "bg-violet-100 text-violet-800" : "bg-slate-100 text-slate-600";
                  return (
                  <div
                    key={announcement.id}
                    className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl bg-slate-50/50 group"
                  >
                    {announcement.urgent && (
                      <AlertCircle size={20} className="text-rose-500 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 text-sm sm:text-base truncate">{announcement.title}</p>
                      <p className="text-xs sm:text-sm text-slate-500">{announcement.date}</p>
                    </div>
                    <span className={`badge text-xs ${badgeClass}`}>{typeLabel}</span>
                    {isSuperAdmin && (
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button type="button" onClick={() => { setEditingAnnouncement(announcement); setModalAnnouncement(true); }} className="p-1.5 hover:bg-slate-200 rounded-lg text-slate-600"><Pencil size={14} /></button>
                        <button type="button" onClick={() => setConfirmModal({ open: true, title: "Eliminar anuncio", message: "¿Eliminar este anuncio?", onConfirm: async () => { await axios.delete(`${API}/announcements/${announcement.id}`, { withCredentials: true }); toast.success("Anuncio eliminado"); fetchGeneralData(); } })} className="p-1.5 hover:bg-rose-100 rounded-lg text-rose-600"><Trash2 size={14} /></button>
                      </div>
                    )}
                  </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <Bell size={32} className="mx-auto mb-2" />
                  <p className="text-sm">No hay anuncios disponibles</p>
                  {isSuperAdmin && (
                    <button onClick={() => { setEditingAnnouncement(null); setModalAnnouncement(true); }} className="btn-primary mt-3 text-sm">Agregar anuncio</button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions Grid - Responsive */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <button
              onClick={() => setActiveTab("avisos")}
              className="glass-panel p-4 sm:p-6 text-center hover:shadow-lg transition-all group"
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto rounded-2xl bg-amber-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Bell size={24} className="text-amber-600 sm:w-7 sm:h-7" />
              </div>
              <p className="font-medium text-slate-800 mt-3 text-sm sm:text-base">Avisos</p>
              <p className="text-xs sm:text-sm text-slate-500">{announcements.length} nuevos</p>
            </button>

            <button
              onClick={() => setActiveTab("calendario")}
              className="glass-panel p-4 sm:p-6 text-center hover:shadow-lg transition-all group"
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Calendar size={24} className="text-primary sm:w-7 sm:h-7" />
              </div>
              <p className="font-medium text-slate-800 mt-3 text-sm sm:text-base">Calendario</p>
              <p className="text-xs sm:text-sm text-slate-500">{upcomingEvents.length} eventos</p>
            </button>

            <button
              onClick={() => setActiveTab("servicios")}
              className="glass-panel p-4 sm:p-6 text-center hover:shadow-lg transition-all group"
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto rounded-2xl bg-emerald-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Briefcase size={24} className="text-emerald-600 sm:w-7 sm:h-7" />
              </div>
              <p className="font-medium text-slate-800 mt-3 text-sm sm:text-base">Servicios</p>
              <p className="text-xs sm:text-sm text-slate-500">{services.length} disponibles</p>
            </button>

            <button
              onClick={() => setShowReportModal(true)}
              className="glass-panel p-4 sm:p-6 text-center hover:shadow-lg transition-all group"
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto rounded-2xl bg-violet-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                <MessageSquare size={24} className="text-violet-600 sm:w-7 sm:h-7" />
              </div>
              <p className="font-medium text-slate-800 mt-3 text-sm sm:text-base">Reportar</p>
              <p className="text-xs sm:text-sm text-slate-500">Sugerencias</p>
            </button>
          </div>
        </div>
      )}

      {/* Avisos Tab */}
      {activeTab === "avisos" && (
        <div className="space-y-6">
          {/* Notices */}
          <div className="glass-panel p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-800">Avisos Importantes</h3>
              {isSuperAdmin && (
                <button onClick={() => { setEditingNotice(null); setModalNotice(true); }} className="btn-primary text-sm py-1.5 px-3 flex items-center gap-1">
                  <Plus size={16} /> Agregar aviso
                </button>
              )}
            </div>
            <div className="space-y-4">
              {notices.length > 0 ? (
                notices.map((notice) => {
                  const isEvento = notice.type === "evento";
                  const isWarning = notice.type === "warning";
                  const typeLabel = NOTICE_TYPE_OPTIONS.find(o => o.value === notice.type)?.label || notice.type;
                  return (
                    <div
                      key={notice.id}
                      className={`p-4 rounded-xl border-l-4 group relative ${
                        isWarning ? "bg-amber-50 border-amber-500" : isEvento ? "bg-violet-50 border-violet-500" : "bg-blue-50 border-blue-500"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {isWarning ? <AlertCircle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" /> : isEvento ? <Calendar size={20} className="text-violet-600 flex-shrink-0 mt-0.5" /> : <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />}
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-medium text-slate-800">{notice.title}</p>
                            {notice.pinned && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium bg-slate-200 text-slate-700"><Pin size={12} /> Fijado</span>}
                            <span className={`text-xs px-2 py-0.5 rounded ${isEvento ? "bg-violet-200 text-violet-800" : isWarning ? "bg-amber-200 text-amber-800" : "bg-blue-200 text-blue-800"}`}>{typeLabel}</span>
                          </div>
                          <p className="text-sm text-slate-600 mt-1">{notice.content}</p>
                          {isEvento && (notice.event_date || notice.event_time) && (
                            <p className="text-sm font-medium text-slate-700 mt-1 flex items-center gap-1">
                              <Clock size={14} /> {[notice.event_date, notice.event_time].filter(Boolean).join(" · ")}
                            </p>
                          )}
                          <p className="text-xs text-slate-400 mt-2">{notice.date}{notice.valid_until ? ` · Válido hasta ${notice.valid_until}` : ""}</p>
                        </div>
                      </div>
                      {isSuperAdmin && (
                        <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button type="button" onClick={() => { setEditingNotice(notice); setModalNotice(true); }} className="p-1.5 hover:bg-slate-200 rounded-lg text-slate-600"><Pencil size={14} /></button>
                          <button type="button" onClick={() => setConfirmModal({ open: true, title: "Eliminar aviso", message: "¿Eliminar este aviso?", onConfirm: async () => { await axios.delete(`${API}/notices/${notice.id}`, { withCredentials: true }); toast.success("Aviso eliminado"); fetchGeneralData(); } })} className="p-1.5 hover:bg-rose-100 rounded-lg text-rose-600"><Trash2 size={14} /></button>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <Info size={32} className="mx-auto mb-2" />
                  <p className="text-sm">No hay avisos disponibles</p>
                  {isSuperAdmin && <button onClick={() => { setEditingNotice(null); setModalNotice(true); }} className="btn-primary mt-3 text-sm">Agregar aviso</button>}
                </div>
              )}
            </div>
          </div>

          {/* Announcements */}
          <div className="glass-panel p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
              <h3 className="font-semibold text-slate-800">Todos los Anuncios</h3>
              <div className="flex items-center gap-2 flex-wrap">
                <select value={filterAnnouncementType} onChange={e => setFilterAnnouncementType(e.target.value)} className="glass-input text-sm py-1.5 px-2 max-w-[180px]">
                  <option value="">Todos</option>
                  {ANNOUNCEMENT_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {isSuperAdmin && (
                  <button onClick={() => { setEditingAnnouncement(null); setModalAnnouncement(true); }} className="btn-primary text-sm py-1.5 px-3 flex items-center gap-1">
                    <Plus size={16} /> Agregar anuncio
                  </button>
                )}
              </div>
            </div>
            <div className="space-y-3">
              {(filterAnnouncementType ? announcements.filter(a => a.type === filterAnnouncementType) : announcements).length > 0 ? (
                (filterAnnouncementType ? announcements.filter(a => a.type === filterAnnouncementType) : announcements).map((announcement) => {
                  const typeLabel = ANNOUNCEMENT_TYPE_OPTIONS.find(o => o.value === announcement.type)?.label || announcement.type;
                  const badgeClass = announcement.type === "evento" ? "badge-primary" : announcement.type === "anuncio" ? "badge-success" : announcement.type === "roles_administrativo" ? "bg-amber-100 text-amber-800" : announcement.type === "general" ? "bg-sky-100 text-sky-800" : announcement.type === "comunicado" ? "bg-violet-100 text-violet-800" : "bg-slate-100 text-slate-600";
                  return (
                    <div key={announcement.id} className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl bg-slate-50/50 hover:bg-slate-100/50 transition-colors group">
                      {announcement.urgent && <span className="w-2 h-2 bg-rose-500 rounded-full flex-shrink-0"></span>}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800 text-sm sm:text-base truncate">{announcement.title}</p>
                        <p className="text-xs sm:text-sm text-slate-500">{announcement.date}</p>
                      </div>
                      <span className={`badge text-xs flex-shrink-0 ${badgeClass}`}>{typeLabel}</span>
                      {isSuperAdmin && (
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button type="button" onClick={() => { setEditingAnnouncement(announcement); setModalAnnouncement(true); }} className="p-1.5 hover:bg-slate-200 rounded-lg text-slate-600"><Pencil size={14} /></button>
                          <button type="button" onClick={() => setConfirmModal({ open: true, title: "Eliminar anuncio", message: "¿Eliminar este anuncio?", onConfirm: async () => { await axios.delete(`${API}/announcements/${announcement.id}`, { withCredentials: true }); toast.success("Anuncio eliminado"); fetchGeneralData(); } })} className="p-1.5 hover:bg-rose-100 rounded-lg text-rose-600"><Trash2 size={14} /></button>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <Bell size={32} className="mx-auto mb-2" />
                  <p className="text-sm">No hay anuncios disponibles</p>
                  {isSuperAdmin && <button onClick={() => { setEditingAnnouncement(null); setModalAnnouncement(true); }} className="btn-primary mt-3 text-sm">Agregar anuncio</button>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Calendario Tab */}
      {activeTab === "calendario" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar Widget */}
          <div className="lg:col-span-2 glass-panel p-4 sm:p-6">
            <CalendarWidget
              events={calendarEvents}
              onSelectDate={handleCalendarDateSelect}
              showSelectedDateEvents={false}
              onAddEvent={isSuperAdmin ? (date) => {
                const today = new Date(); today.setHours(0,0,0,0);
                const d = new Date(date); d.setHours(0,0,0,0);
                if (d < today) { toast.error("No se pueden crear eventos en fechas pasadas"); return; }
                setEditingEvent(null); setEventDatePreset(date);
                setFormEvent(prev => ({ ...prev, date: date.getFullYear() + "-" + String(date.getMonth() + 1).padStart(2, "0") + "-" + String(date.getDate()).padStart(2, "0") }));
                setModalEvent(true);
              } : undefined}
              onEditEvent={isSuperAdmin ? (event) => { setEditingEvent(event); setModalEvent(true); } : undefined}
              onDeleteEvent={isSuperAdmin ? (event) => setConfirmModal({ open: true, title: "Eliminar evento", message: "¿Eliminar este evento?", onConfirm: async () => { await axios.delete(`${API}/calendar/events/${event.id}`, { withCredentials: true }); toast.success("Evento eliminado"); fetchGeneralData(); } }) : undefined}
              onEventClick={(event) => setEventDetailModal(event)}
            />
          </div>

          {/* Columna derecha: 2 recuadros - Próximos Eventos y Eventos del día seleccionado */}
          <div className="space-y-6">
            {/* Recuadro 1: Próximos Eventos */}
            <div className="glass-panel p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-800">Próximos Eventos</h3>
                {isSuperAdmin && (
                  <button onClick={() => { setEditingEvent(null); setModalEvent(true); }} className="btn-primary text-sm py-1.5 px-3 flex items-center gap-1">
                    <Plus size={16} /> Agregar evento
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {upcomingEvents.map((event) => (
                  <div
                    key={event.id}
                    onClick={() => setEventDetailModal(event)}
                    className="p-4 rounded-xl bg-slate-50/50 hover:bg-slate-100/50 transition-colors group relative cursor-pointer"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex flex-col items-center justify-center flex-shrink-0">
                        <span className="text-lg font-bold text-primary">{parseDateLocal(event.date)?.getDate()}</span>
                        <span className="text-[10px] text-primary uppercase">{parseDateLocal(event.date)?.toLocaleDateString("es-ES", { month: "short" })}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800 text-sm">{event.title}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                          <Clock size={12} />
                          <span>{event.time || "—"}</span>
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5">{event.location || "—"}</p>
                        {event.description && (
                          <p className="text-xs text-slate-500 mt-1 truncate">
                            {/^https?:\/\//.test(String(event.description).trim()) ? (
                              <a href={event.description.trim()} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-primary hover:underline break-all">{(event.description || "").trim()}</a>
                            ) : (
                              event.description
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                    {isSuperAdmin && (
                      <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                        <button type="button" onClick={() => { setEditingEvent(event); setModalEvent(true); }} className="p-1.5 hover:bg-slate-200 rounded-lg text-slate-600"><Pencil size={14} /></button>
                        <button type="button" onClick={() => setConfirmModal({ open: true, title: "Eliminar evento", message: "¿Eliminar este evento?", onConfirm: async () => { await axios.delete(`${API}/calendar/events/${event.id}`, { withCredentials: true }); toast.success("Evento eliminado"); fetchGeneralData(); } })} className="p-1.5 hover:bg-rose-100 rounded-lg text-rose-600"><Trash2 size={14} /></button>
                      </div>
                    )}
                  </div>
                ))}
                {upcomingEvents.length === 0 && (
                  <div className="text-center py-8 text-slate-400">
                    <Calendar size={32} className="mx-auto mb-2" />
                    <p className="text-sm">No hay eventos próximos</p>
                    {isSuperAdmin && <button onClick={() => { setEditingEvent(null); setModalEvent(true); }} className="btn-primary mt-3 text-sm">Agregar evento</button>}
                  </div>
                )}
              </div>
            </div>

            {/* Recuadro 2: Eventos del día seleccionado (o de hoy si no hay selección) */}
            <div className="glass-panel p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-800">Eventos del día</h3>
                {isSuperAdmin && (
                  <button
                    onClick={() => {
                      setEditingEvent(null);
                      const dateForForm = selectedCalendarDate
                        ? (displayDate.getFullYear() + "-" + String(displayDate.getMonth() + 1).padStart(2, "0") + "-" + String(displayDate.getDate()).padStart(2, "0"))
                        : todayDateString();
                      setEventDatePreset(selectedCalendarDate || new Date());
                      setFormEvent(prev => ({ ...prev, date: dateForForm, title: "", time: "", location: "", description: "" }));
                      setModalEvent(true);
                    }}
                    className="btn-primary text-sm py-1.5 px-3 flex items-center gap-1"
                  >
                    <Plus size={16} /> Agregar
                  </button>
                )}
              </div>
              <p className="text-sm text-slate-500 mb-3">{displayDateLabel}</p>
              <div className="space-y-2">
                {displayDayEvents.length > 0 ? (
                  displayDayEvents.map((event) => (
                    <div
                      key={event.id}
                      onClick={() => setEventDetailModal(event)}
                      className="p-3 rounded-xl bg-slate-50/50 hover:bg-slate-100/50 transition-colors group relative cursor-pointer"
                    >
                      <p className="font-medium text-slate-800 text-sm">{event.title}</p>
                      {event.time && <p className="text-xs text-slate-500 mt-1">{event.time}</p>}
                      {event.location && <p className="text-xs text-slate-400">{event.location}</p>}
                      {event.description && (
                        <p className="text-xs text-slate-500 mt-1 truncate">
                          {/^https?:\/\//.test(String(event.description).trim()) ? (
                            <a href={event.description.trim()} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-primary hover:underline break-all">{(event.description || "").trim()}</a>
                          ) : (
                            event.description
                          )}
                        </p>
                      )}
                      {isSuperAdmin && (
                        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                          <button type="button" onClick={() => { setEditingEvent(event); setModalEvent(true); }} className="p-1.5 hover:bg-slate-200 rounded-lg text-slate-600"><Pencil size={14} /></button>
                          <button type="button" onClick={() => setConfirmModal({ open: true, title: "Eliminar evento", message: "¿Eliminar este evento?", onConfirm: async () => { await axios.delete(`${API}/calendar/events/${event.id}`, { withCredentials: true }); toast.success("Evento eliminado"); fetchGeneralData(); } })} className="p-1.5 hover:bg-rose-100 rounded-lg text-rose-600"><Trash2 size={14} /></button>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-400 text-center py-6">No hay eventos este día</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Servicios Tab */}
      {activeTab === "servicios" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800">Servicios</h3>
            {isSuperAdmin && (
              <button onClick={() => { setEditingService(null); setModalService(true); }} className="btn-primary text-sm py-1.5 px-3 flex items-center gap-1">
                <Plus size={16} /> Agregar servicio
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {services.length > 0 ? (
              services.map((service) => {
                const IconComponent = typeof service.icon === "function" ? service.icon :
                  service.icon === "Briefcase" ? Briefcase :
                  service.icon === "User" ? User :
                  service.icon === "Target" ? Target :
                  service.icon === "MessageSquare" ? MessageSquare : Briefcase;
                return (
                  <div key={service.id} className="glass-panel p-4 sm:p-6 hover:shadow-lg transition-all group relative">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <IconComponent size={24} className="text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-slate-800">{service.name}</h4>
                        <p className="text-sm text-slate-500 mt-1">{service.description}</p>
                        <a href={`mailto:${service.contact}`} className="text-primary text-sm hover:underline mt-2 inline-block truncate max-w-full">{service.contact}</a>
                      </div>
                    </div>
                    {isSuperAdmin && (String(service.id).startsWith("svc_") ? (
                      <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button type="button" onClick={() => { setEditingService(service); setModalService(true); }} className="p-1.5 hover:bg-slate-200 rounded-lg text-slate-600"><Pencil size={14} /></button>
                        <button type="button" onClick={() => setConfirmModal({ open: true, title: "Eliminar servicio", message: "¿Eliminar este servicio?", onConfirm: async () => { await axios.delete(`${API}/services/${service.id}`, { withCredentials: true }); toast.success("Servicio eliminado"); fetchGeneralData(); } })} className="p-1.5 hover:bg-rose-100 rounded-lg text-rose-600"><Trash2 size={14} /></button>
                      </div>
                    ) : null)}
                  </div>
                );
              })
            ) : (
              <div className="col-span-2 text-center py-8 text-slate-400">
                <Briefcase size={32} className="mx-auto mb-2" />
                <p className="text-sm">No hay servicios disponibles</p>
                {isSuperAdmin && <button onClick={() => { setEditingService(null); setModalService(true); }} className="btn-primary mt-3 text-sm">Agregar servicio</button>}
              </div>
            )}
          </div>

          {/* Report/Suggestion Card */}
          <div className="glass-panel p-4 sm:p-6 bg-gradient-to-br from-primary/5 to-secondary/5">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-violet-100 flex items-center justify-center flex-shrink-0">
                <MessageSquare size={28} className="text-violet-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-800">¿Tienes algo que reportar?</h3>
                <p className="text-sm text-slate-600 mt-1">
                  Envía sugerencias, reporta problemas o comparte ideas para mejorar.
                </p>
              </div>
              <button
                onClick={() => setShowReportModal(true)}
                className="btn-primary w-full sm:w-auto"
                data-testid="open-report-btn"
              >
                Enviar Reporte
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SuperAdmin CRUD Modals */}
      {modalAnnouncement && createPortal(
        <div className="fixed inset-0 min-h-[100dvh] h-[100dvh] bg-slate-900/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="glass-panel p-6 w-full max-w-md">
            <h2 className="text-lg font-bold text-slate-800 mb-4">{editingAnnouncement ? "Editar anuncio" : "Agregar anuncio"}</h2>
            <div className="space-y-3">
              <input value={formAnnouncement.title} onChange={e => setFormAnnouncement(f => ({ ...f, title: e.target.value }))} className="w-full glass-input" placeholder="Título" />
              <input value={formAnnouncement.date} onChange={e => setFormAnnouncement(f => ({ ...f, date: e.target.value }))} className="w-full glass-input" placeholder="Fecha (ej. 02/02/2025)" />
              <select value={formAnnouncement.type} onChange={e => setFormAnnouncement(f => ({ ...f, type: e.target.value }))} className="w-full glass-input">
                {ANNOUNCEMENT_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formAnnouncement.urgent} onChange={e => setFormAnnouncement(f => ({ ...f, urgent: e.target.checked }))} /> Urgente</label>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setModalAnnouncement(false)} className="btn-secondary">Cancelar</button>
              <button disabled={saving || !formAnnouncement.title.trim()} onClick={async () => { setSaving(true); try { if (editingAnnouncement) await axios.put(`${API}/announcements/${editingAnnouncement.id}`, formAnnouncement, { withCredentials: true }); else await axios.post(`${API}/announcements`, formAnnouncement, { withCredentials: true }); toast.success(editingAnnouncement ? "Anuncio actualizado" : "Anuncio creado"); fetchGeneralData(); window.dispatchEvent(new CustomEvent("app:refresh-notifications")); setModalAnnouncement(false); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setSaving(false); } }} className="btn-primary">{saving ? "Guardando…" : "Guardar"}</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {modalNotice && createPortal(
        <div className="fixed inset-0 min-h-[100dvh] h-[100dvh] bg-slate-900/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="glass-panel p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold text-slate-800 mb-4">{editingNotice ? "Editar aviso" : "Agregar aviso"}</h2>
            <div className="space-y-3">
              <input value={formNotice.title} onChange={e => setFormNotice(f => ({ ...f, title: e.target.value }))} className="w-full glass-input" placeholder="Título" />
              <textarea value={formNotice.content} onChange={e => setFormNotice(f => ({ ...f, content: e.target.value }))} className="w-full glass-input min-h-[80px]" placeholder="Contenido" />
              <input value={formNotice.date} onChange={e => setFormNotice(f => ({ ...f, date: e.target.value }))} className="w-full glass-input" placeholder="Fecha (ej. 02/02/2025)" />
              <label className="block text-sm font-medium text-slate-700">Tipo</label>
              <select value={formNotice.type} onChange={e => setFormNotice(f => ({ ...f, type: e.target.value }))} className="w-full glass-input">
                {NOTICE_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={formNotice.pinned} onChange={e => setFormNotice(f => ({ ...f, pinned: e.target.checked }))} />
                <span>Fijar (mantener arriba)</span>
              </label>
              <div>
                <label className="block text-sm font-medium text-slate-700">Válido hasta (opcional)</label>
                <input type="date" value={formNotice.valid_until} onChange={e => setFormNotice(f => ({ ...f, valid_until: e.target.value }))} className="w-full glass-input" placeholder="YYYY-MM-DD" />
              </div>
              {formNotice.type === "evento" && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Fecha del evento</label>
                    <input type="date" value={formNotice.event_date} onChange={e => setFormNotice(f => ({ ...f, event_date: e.target.value }))} className="w-full glass-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Hora del evento</label>
                    <input value={formNotice.event_time} onChange={e => setFormNotice(f => ({ ...f, event_time: e.target.value }))} className="w-full glass-input" placeholder="ej. 10:00" />
                  </div>
                </>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setModalNotice(false)} className="btn-secondary">Cancelar</button>
              <button disabled={saving || !formNotice.title.trim() || !formNotice.content.trim()} onClick={async () => { setSaving(true); try { const payload = { ...formNotice, valid_until: formNotice.valid_until || null, event_date: formNotice.event_date || null, event_time: formNotice.event_time || null }; if (editingNotice) await axios.put(`${API}/notices/${editingNotice.id}`, payload, { withCredentials: true }); else await axios.post(`${API}/notices`, payload, { withCredentials: true }); toast.success(editingNotice ? "Aviso actualizado" : "Aviso creado"); fetchGeneralData(); window.dispatchEvent(new CustomEvent("app:refresh-notifications")); setModalNotice(false); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setSaving(false); } }} className="btn-primary">{saving ? "Guardando…" : "Guardar"}</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {modalService && createPortal(
        <div className="fixed inset-0 min-h-[100dvh] h-[100dvh] bg-slate-900/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="glass-panel p-6 w-full max-w-md">
            <h2 className="text-lg font-bold text-slate-800 mb-4">{editingService ? "Editar servicio" : "Agregar servicio"}</h2>
            <div className="space-y-3">
              <input value={formService.name} onChange={e => setFormService(f => ({ ...f, name: e.target.value }))} className="w-full glass-input" placeholder="Nombre" />
              <input value={formService.description} onChange={e => setFormService(f => ({ ...f, description: e.target.value }))} className="w-full glass-input" placeholder="Descripción" />
              <input value={formService.contact} onChange={e => setFormService(f => ({ ...f, contact: e.target.value }))} className="w-full glass-input" placeholder="Email de contacto" />
              <select value={formService.icon} onChange={e => setFormService(f => ({ ...f, icon: e.target.value }))} className="w-full glass-input">
                <option value="Briefcase">Briefcase</option>
                <option value="User">User</option>
                <option value="Target">Target</option>
                <option value="MessageSquare">MessageSquare</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setModalService(false)} className="btn-secondary">Cancelar</button>
              <button disabled={saving || !formService.name.trim() || !formService.contact.trim()} onClick={async () => { setSaving(true); try { if (editingService) await axios.put(`${API}/services/${editingService.id}`, formService, { withCredentials: true }); else await axios.post(`${API}/services`, formService, { withCredentials: true }); toast.success(editingService ? "Servicio actualizado" : "Servicio creado"); fetchGeneralData(); setModalService(false); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setSaving(false); } }} className="btn-primary">{saving ? "Guardando…" : "Guardar"}</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {modalEvent && createPortal(
        <div className="fixed inset-0 min-h-[100dvh] h-[100dvh] bg-slate-900/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="glass-panel p-6 w-full max-w-md">
            <h2 className="text-lg font-bold text-slate-800 mb-4">{editingEvent ? "Editar evento" : "Agregar evento"}</h2>
            <div className="space-y-3">
              <input value={formEvent.title} onChange={e => setFormEvent(f => ({ ...f, title: e.target.value }))} className="w-full glass-input" placeholder="Título" />
              <input type="date" value={formEvent.date} min={editingEvent ? undefined : todayDateString()} onChange={e => setFormEvent(f => ({ ...f, date: e.target.value }))} className="w-full glass-input" />
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">Hora</label>
                <input type="time" required value={formEvent.time ? String(formEvent.time).slice(0, 5) : ""} onChange={e => setFormEvent(f => ({ ...f, time: e.target.value }))} className="w-full glass-input" />
              </div>
              <input value={formEvent.location} onChange={e => setFormEvent(f => ({ ...f, location: e.target.value }))} className="w-full glass-input" placeholder="Ubicación" />
              <textarea value={formEvent.description} onChange={e => setFormEvent(f => ({ ...f, description: e.target.value }))} className="w-full glass-input min-h-[80px]" placeholder="Descripción o enlace (ej. Meet, Zoom)" />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => { setModalEvent(false); setEventDatePreset(null); }} className="btn-secondary">Cancelar</button>
              <button disabled={saving || !formEvent.title.trim() || !formEvent.date || !formEvent.time.trim()} onClick={async () => { setSaving(true); try { if (editingEvent) await axios.put(`${API}/calendar/events/${editingEvent.id}`, formEvent, { withCredentials: true }); else await axios.post(`${API}/calendar/events`, formEvent, { withCredentials: true }); toast.success(editingEvent ? "Evento actualizado" : "Evento creado"); fetchGeneralData(); setModalEvent(false); setEventDatePreset(null); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setSaving(false); } }} className="btn-primary">{saving ? "Guardando…" : "Guardar"}</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Carousel modal: list slides or form for add/edit */}
      {modalCarouselSlide && createPortal(
        <div className="fixed inset-0 min-h-[100dvh] h-[100dvh] bg-slate-900/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="glass-panel p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {editingCarouselSlide !== undefined ? (
              <>
                <h2 className="text-lg font-bold text-slate-800 mb-4">{editingCarouselSlide ? "Editar diapositiva" : "Agregar diapositiva"}</h2>
                <div className="space-y-3">
                  <input value={formCarouselSlide.url} onChange={e => setFormCarouselSlide(f => ({ ...f, url: e.target.value }))} className="w-full glass-input" placeholder="URL de imagen" />
                  <input value={formCarouselSlide.title} onChange={e => setFormCarouselSlide(f => ({ ...f, title: e.target.value }))} className="w-full glass-input" placeholder="Título" />
                  <input value={formCarouselSlide.description} onChange={e => setFormCarouselSlide(f => ({ ...f, description: e.target.value }))} className="w-full glass-input" placeholder="Descripción" />
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <button onClick={() => setEditingCarouselSlide(undefined)} className="btn-secondary">Cancelar</button>
                  <button disabled={saving || !formCarouselSlide.url.trim() || !formCarouselSlide.title.trim()} onClick={async () => { setSaving(true); try { const isBackendSlide = editingCarouselSlide && !String(editingCarouselSlide.id || "").startsWith("default-"); if (isBackendSlide) { await axios.put(`${API}/carousel/${editingCarouselSlide.id}`, formCarouselSlide, { withCredentials: true }); toast.success("Diapositiva actualizada"); } else { await axios.post(`${API}/carousel`, formCarouselSlide, { withCredentials: true }); toast.success(editingCarouselSlide ? "Diapositiva actualizada" : "Diapositiva creada"); } fetchGeneralData(); setEditingCarouselSlide(undefined); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setSaving(false); } }} className="btn-primary">{saving ? "Guardando…" : "Guardar"}</button>
                </div>
              </>
            ) : (    
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-slate-800">Gestionar carrusel</h2>
                  <button onClick={() => setModalCarouselSlide(false)} className="p-2 hover:bg-slate-100 rounded-lg"><X size={20} /></button>
                </div>
                <div className="space-y-2 mb-4">
                  {(carouselSlides.length > 0 ? carouselSlides : defaultCarouselSlides.filter((d) => !removedDefaultIds.includes(d.id))).map((slide) => {
                    const isDefault = String(slide.id || "").startsWith("default-");
                    return (
                      <div key={slide.id} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
                        <img src={slide.url} alt="" className="w-16 h-10 object-cover rounded" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 text-sm truncate">{slide.title}</p>
                          <p className="text-xs text-slate-500 truncate">{slide.description}</p>
                          {isDefault && <span className="text-xs text-slate-400">Predeterminada</span>}
                        </div>
                        <div className="flex gap-1">
                          <button type="button" onClick={() => setEditingCarouselSlide(slide)} className="p-1.5 hover:bg-slate-200 rounded-lg text-slate-600" title={isDefault ? "Editar y guardar crea una nueva en el carrusel" : "Editar"}><Pencil size={14} /></button>
                          <button type="button" onClick={() => isDefault ? setConfirmModal({ open: true, title: "Ocultar predeterminada", message: "Esta diapositiva predeterminada se quitará del carrusel hasta que recargues la página.", onConfirm: () => setRemovedDefaultIds((prev) => [...prev, slide.id]) }) : setConfirmModal({ open: true, title: "Eliminar diapositiva", message: "¿Eliminar esta diapositiva del carrusel?", onConfirm: async () => { await axios.delete(`${API}/carousel/${slide.id}`, { withCredentials: true }); toast.success("Diapositiva eliminada"); fetchGeneralData(); } })} className="p-1.5 hover:bg-rose-100 rounded-lg text-rose-600"><Trash2 size={14} /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button onClick={() => setEditingCarouselSlide(null)} className="btn-primary w-full flex items-center justify-center gap-2">
                  <Plus size={18} /> Agregar diapositiva
                </button>
              </>
            )}
          </div>
        </div>,
        document.body
      )}

      <ConfirmModal
        open={confirmModal.open}
        onClose={() => setConfirmModal(c => ({ ...c, open: false }))}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
      />

      {eventDetailModal && createPortal(
        <div className="fixed inset-0 min-h-[100dvh] h-[100dvh] bg-slate-900/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={() => setEventDetailModal(null)}>
          <div className="glass-panel p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-800">{eventDetailModal.title}</h3>
              <button type="button" onClick={() => setEventDetailModal(null)} className="p-2 hover:bg-slate-100 rounded-lg"><X size={20} /></button>
            </div>
            {eventDetailModal.time && <p className="text-sm text-slate-500 mb-1">Hora: {eventDetailModal.time}</p>}
            {eventDetailModal.location && <p className="text-sm text-slate-500 mb-2">Lugar: {eventDetailModal.location}</p>}
            {eventDetailModal.description ? (
              <div className="text-sm text-slate-600 whitespace-pre-wrap break-words">
                {linkifyDescription(eventDetailModal.description)}
              </div>
            ) : (
              <p className="text-sm text-slate-400">Sin descripción</p>
            )}
            <div className="mt-4 pt-4 border-t border-slate-100">
              <button
                type="button"
                onClick={() => handleAddToGoogleCalendar(eventDetailModal)}
                disabled={addingToGoogleCalendar}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium transition-colors disabled:opacity-50"
              >
                <CalendarPlus size={18} />
                {addingToGoogleCalendar ? "Añadiendo…" : "Añadir a mi Google Calendar"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Report Modal */}
      {showReportModal && createPortal(
        <div className="fixed inset-0 min-h-[100dvh] h-[100dvh] bg-slate-900/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="glass-panel p-6 sm:p-8 w-full max-w-lg animate-slide-up" data-testid="report-modal">
            {reportSubmitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                  <CheckCircle size={32} className="text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">¡Enviado!</h3>
                <p className="text-slate-600">Tu reporte ha sido enviado correctamente.</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-slate-800">Enviar Reporte</h2>
                  <button
                    onClick={() => setShowReportModal(false)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>

                <p className="text-sm text-slate-500 mb-4">
                  Los reportes se guardan y el equipo los revisa. Para temas urgentes:{" "}
                  <a href="mailto:contacto@ecosistemawca.com" className="text-primary hover:underline">contacto@ecosistemawca.com</a>
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Tipo de reporte
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { id: "sugerencia", label: "Sugerencia" },
                        { id: "problema", label: "Problema" },
                        { id: "idea", label: "Idea" },
                        { id: "otro", label: "Otro" },
                      ].map((type) => (
                        <button
                          key={type.id}
                          onClick={() => setReportType(type.id)}
                          className={`p-3 rounded-xl border-2 transition-all ${
                            reportType === type.id
                              ? "border-primary bg-primary/5"
                              : "border-slate-200 hover:border-primary/50"
                          }`}
                        >
                          {type.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Descripción
                    </label>
                    <textarea
                      value={reportContent}
                      onChange={(e) => setReportContent(e.target.value)}
                      className="w-full glass-input min-h-[120px]"
                      placeholder="Describe tu reporte o sugerencia..."
                      data-testid="report-content-input"
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
                    <button
                      onClick={() => setShowReportModal(false)}
                      className="btn-secondary order-2 sm:order-1"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleSubmitReport}
                      disabled={!reportContent.trim() || reportSending}
                      className="btn-primary flex items-center justify-center gap-2 order-1 sm:order-2"
                      data-testid="submit-report-btn"
                    >
                      <Send size={18} />
                      {reportSending ? "Enviando…" : "Enviar"}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default InformacionGeneral;
